import MarketGroupInfo from '@models/struct/MarketGroupInfo';
import { MarketForArrange } from '@modules/eventGrid/eventRowUtils';
import { MappedMarket } from '../MarketLayouts/MarketLayoutTypes';
import { BetBuilderMarket } from '@sportOfferFederation/bet-builder/types/BetBuilderMarket';
import { getUniqueMarketId } from '@sportOfferFederation/bet-builder/utils/getUniqueMarketId';

export const ALL_MARKETS_GROUP_ID = -1;
export const BET_BUILDER_GROUP_ID = -2;

export interface MarketGroup {
    id: number;
    name: string;
    markets: (MarketForArrange | MappedMarket)[];
}

export const isMappedMarket = <T extends Record<string, unknown>>(market: T | MappedMarket): market is MappedMarket => 'includedMarkets' in market;

export function groupMarkets(
    marketGroups: MarketGroupInfo[],
    markets: (MarketForArrange | MappedMarket)[],
    allMarketsName: string,
    betBuilder: { name: string; isAvailable: boolean } | null,
    allowedMarketGroupIds?: Set<number>,
): Map<number, MarketGroup> {
    const allowedMarketGroups = allowedMarketGroupIds
        ? marketGroups.filter((group) => allowedMarketGroupIds.has(group.id))
        : marketGroups;
    const mapper = getMarketGroupMapper(markets);
    const groupedMarkets = allowedMarketGroups.map(mapper).filter((group) => group.markets.length);

    // Used for filtering all markets
    const allowedMarkets = allowedMarketGroupIds && getUnionOfAllMarketIds(allowedMarketGroups);

    // Remove duplicate bet builder group, it's a list of markets that are created from combined odds in the BE
    // It should not be accessible to customers, whether the FF is on or off, and the BE should hopefully hide it soon.
    if (betBuilder?.name) {
        const syntheticBetBuilderGroupIndex = groupedMarkets.findIndex((group) => group.name === betBuilder.name);

        if (syntheticBetBuilderGroupIndex !== -1) {
            groupedMarkets.splice(syntheticBetBuilderGroupIndex, 1);
        }
    }

    if (betBuilder?.isAvailable) {
        // Add synthetic bet builder group (possibly replacing the one removed above)
        groupedMarkets.unshift(getBetBuilderGroup(betBuilder.name));
    }
    // ensuring we always have an "All Markets" group (even when there are no markets available)
    groupedMarkets.unshift(getAllMarketsGroup(allMarketsName, markets, allowedMarkets));

    // using a map to preserve the group order
    return new Map(groupedMarkets.map((group) => [group.id, group]));
}

export function getMarketGroupMapper(markets: (MarketForArrange | MappedMarket)[]) {
    return (groupInfo: MarketGroupInfo) => {
        return {
            id: groupInfo.id,
            name: groupInfo.name,
            markets: getFilteredMarketsForMarketGroup(markets, groupInfo.marketIds),
        };
    };
}

export function getAllMarketsGroup(
    name: string,
    markets: (MarketForArrange | MappedMarket)[],
    allowedMarkets?: Set<number>,
): MarketGroup {
    return {
        id: ALL_MARKETS_GROUP_ID,
        name,
        markets: allowedMarkets
            ? getFilteredMarketsForMarketGroup(markets, allowedMarkets)
            : markets,
    };
}

function getFilteredMarketsForMarketGroup(markets: (MarketForArrange | MappedMarket)[], allowedMarkets: Set<number>) {
    return markets.filter((market) => {
        // check mapped markets that are grouped from multiple markets
        if (isMappedMarket(market) && market.hasNestedMarkets) {
            return !!market.includedMarkets.find(({ id }) => allowedMarkets.has(id));
        }

        // check regular markets
        for (const odd of market.odds) {
            if (allowedMarkets.has(odd.marketId)) {
                return true;
            }
        }

        return allowedMarkets.has(market.id!);
    });
}

export function getBetBuilderGroup(name: string): MarketGroup {
    return {
        id: BET_BUILDER_GROUP_ID,
        name,
        markets: [],
    };
}

export function getUnionOfAllMarketIds(marketGroups: MarketGroupInfo[]) {
    return new Set(
        marketGroups
            .map((group) => group.marketIds)
            .reduce((acc, ids) => {
                // eslint-disable-next-line no-param-reassign
                acc = [...acc, ...ids];
                return acc;
            }, [] as number[]),
    );
}

export function getSuperbetsGroupId(
    marketGroups: MarketGroupInfo[],
    markets: Array<Pick<MarketForArrange, 'id' | 'isSuperbet' | 'odds'>>,
): MarketGroupInfo['id'] | null {
    const market = markets.find((market) => market.isSuperbet);
    if (!market) {
        return null;
    }

    const marketGroup = marketGroups.find((marketGroup) =>
        market.odds.some(({ marketId }) => marketGroup.marketIds.has(marketId)),
    );
    if (!marketGroup) {
        return null;
    }

    return marketGroup.id;
}

export function createExpandedMarketIds(markets: (MarketForArrange | MappedMarket)[]) {
    return markets
        .reduce((acc: Record<string, boolean>, market) => {
            acc[market.uniqueId!] = true;
            return acc;
        }, {});
}

export function createBetBuilderExpandedMarketIds(markets: BetBuilderMarket[]) {
    return markets
        .reduce((acc: Record<string, boolean>, market) => {
            acc[getUniqueMarketId(market)] = true;
            return acc;
        }, {});
}
